import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Clock } from 'lucide-react';
import { Button } from './ui/Button';
import { BakeryEvent } from '../types/event';
import { INFO_EMAIL, sendInfoMail } from '../utils/infoMail';
interface EventSignupModalProps {
  event: BakeryEvent;
  onClose: () => void;
}
const inputClass = 'w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-[border-color,box-shadow] text-sm';
const labelClass = 'block text-xs font-medium text-[#1A1A1A]/70 mb-1.5';
export function EventSignupModal({
  event,
  onClose
}: EventSignupModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const dateLabel = new Date(`${event.date}T12:00:00`).toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const fullName = `${firstName.trim()} ${lastName.trim()}`;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const message = [`════════════════════════════════`, `   NY ANMÄLAN`, `════════════════════════════════`, '', `▸ EVENT:      ${event.title}`, `▸ DATUM:      ${event.date} (${dateLabel})`, `▸ TID:        ${event.time}`, ...(event.price ? [`▸ PRIS:       ${event.price}`] : []), '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   DELTAGARE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Förnamn:   ${firstName.trim()}`, `  Efternamn: ${lastName.trim()}`, `  E-post:    ${email.trim()}`, `  Telefon:   ${phone.trim()}`, '', `════════════════════════════════`, `Svara direkt på detta mejl för att nå deltagaren.`, `════════════════════════════════`].join('\n');
    const ok = await sendInfoMail({
      subject: `[ANMÄLAN] ${event.title} – ${event.date} – ${fullName}`,
      message,
      fromName: `Top Notch Anmälan – ${fullName}`,
      replyTo: email.trim()
    });
    setIsSubmitting(false);
    if (ok) {
      setSent(true);
    } else {
      setError(`Något gick fel. Försök igen eller mejla oss på ${INFO_EMAIL}.`);
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-470" data-mp="3iih5v2cwesqx">
      <motion.div role="dialog" aria-modal="true" aria-labelledby="event-signup-title" initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={e => e.stopPropagation()} className="bg-[#F2EDE4] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-8" data-id="element-471" data-mp="1zjj94gn87t0j">
        <div className="bg-white px-6 py-5 border-b border-[#7C8062]/15 flex items-start justify-between gap-4" data-id="element-472" data-mp="1gsia5sm3vxgz">
          <div className="min-w-0" data-id="element-473" data-mp="1qkfov9cbp7kg">
            <p className="text-xs font-medium text-[#7C8062] mb-1" data-id="element-474" data-mp="1qlhdyhq6bul6">
              {event.ctaLabel ?? 'Anmäl dig'} här
            </p>
            <h2 id="event-signup-title" className="text-xl font-serif text-[#1A1A1A] leading-tight" data-id="element-475" data-mp="1lseiu55r0d2v">
              {event.title}
            </h2>
            <p className="mt-1.5 text-sm text-[#1A1A1A]/60 flex items-center gap-1.5 capitalize" data-id="element-476" data-mp="t4imj1k3cn6m">
              <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" data-id="element-477" data-mp="3skm71i27tokq" />
              {dateLabel} · {event.time}
            </p>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-1 text-[#1A1A1A]/50 hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-478" data-mp="5elfh8k7hwd0">
            <X className="w-5 h-5" data-id="element-479" data-mp="f33bzdlgdhau" />
          </button>
        </div>

        <div className="p-6" data-id="element-480" data-mp="k2zf7ambt4pc">
          {!sent ? <form onSubmit={handleSubmit} className="space-y-4" data-id="element-481" data-mp="k1oczf8j450c">
              <div className="grid grid-cols-2 gap-4" data-id="element-482" data-mp="o4l27oskqzd9">
                <div data-id="element-483" data-mp="o305bsisf4c8">
                  <label htmlFor="su-first" className={labelClass} data-id="element-484" data-mp="t3ntm6fmt72r">
                    Förnamn *
                  </label>
                  <input id="su-first" type="text" required autoComplete="given-name" value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} data-id="element-485" data-mp="2px97e3os3wvo" />
                </div>
                <div data-id="element-486" data-mp="2ij8ae1yo6f3d">
                  <label htmlFor="su-last" className={labelClass} data-id="element-487" data-mp="2dlf6jfku9x69">
                    Efternamn *
                  </label>
                  <input id="su-last" type="text" required autoComplete="family-name" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} data-id="element-488" data-mp="28nwu3f7m4zti" />
                </div>
              </div>
              <div data-id="element-489" data-mp="2sb4j06ydtnlu">
                <label htmlFor="su-email" className={labelClass} data-id="element-490" data-mp="2s9t71jus99jn">
                  E-post *
                </label>
                <input id="su-email" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="din@epost.se" data-id="element-491" data-mp="1ky2mpd62c3gn" />
              </div>
              <div data-id="element-492" data-mp="18f6zb89wmkhq">
                <label htmlFor="su-phone" className={labelClass} data-id="element-493" data-mp="18dvst0p2qymx">
                  Telefon *
                </label>
                <input id="su-phone" type="tel" required autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} placeholder="070-123 45 67" data-id="element-494" data-mp="1dc75dj6r7n8u" />
              </div>

              {error && <p role="alert" className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-100" data-id="element-495" data-mp="unsb8n153t3t">
                  {error}
                </p>}

              <div className="pt-2" data-id="element-496" data-mp="kqxgmf7zmcut">
                <Button type="submit" isLoading={isSubmitting} className="w-full py-3.5 text-base" data-id="element-497" data-mp="200r8cvjkdom">
                  {event.ctaLabel ?? 'Skicka anmälan'}
                </Button>
                {event.price && <p className="text-center text-xs text-[#1A1A1A]/50 mt-3" data-id="element-498" data-mp="61w2oa76s7g4">
                    Pris {event.price}. Vi bekräftar din plats via mejl.
                  </p>}
              </div>
            </form> : <div className="text-center py-6 space-y-4" data-id="element-499" data-mp="g0230ndch5iv">
              <div className="w-14 h-14 bg-[#7C8062]/10 rounded-full flex items-center justify-center mx-auto" data-id="element-500" data-mp="tshzf5bqq5at">
                <CheckCircle2 className="w-7 h-7 text-[#7C8062]" data-id="element-501" data-mp="pq4ugj4tibf3" />
              </div>
              <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-502" data-mp="13pmmd7deu4so">
                Tack, {firstName.trim()}!
              </h3>
              <p className="text-[#1A1A1A]/70 max-w-sm mx-auto text-sm leading-relaxed" data-id="element-503" data-mp="yse313gizupu">
                Vi har tagit emot din anmälan till {event.title}. Du får en
                bekräftelse från oss via mejl.
              </p>
              <div className="pt-4" data-id="element-504" data-mp="2a76wu4lhy69p">
                <Button onClick={onClose} className="w-full sm:w-auto px-8" data-id="element-505" data-mp="2f3oxotdphkgr">
                  Stäng
                </Button>
              </div>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}